import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { errorMessage } from "@/lib/errors";
import * as slotsApi from "@/services/api/slots";
import type { AvailabilityQuery, BookingCreate, ChangeRequestCreate } from "@/services/api/slots";

export function useAvailableSlots(query: AvailabilityQuery | null) {
  return useQuery({
    queryKey: ["slots", "available", query],
    queryFn: () => slotsApi.availableSlots(query as AvailabilityQuery),
    enabled: query !== null,
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BookingCreate) => slotsApi.createBooking(data),
    onSuccess: () => {
      toast.success("Slot booked");
      void queryClient.invalidateQueries({ queryKey: ["loads"] });
      void queryClient.invalidateQueries({ queryKey: ["slots"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useCreateChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ChangeRequestCreate) => slotsApi.createChangeRequest(data),
    onSuccess: () => {
      toast.success("Slot proposal submitted");
      void queryClient.invalidateQueries({ queryKey: ["slots", "change-requests"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useChangeRequests(status?: string) {
  return useQuery({
    queryKey: ["slots", "change-requests", status],
    queryFn: () => slotsApi.listChangeRequests(status),
  });
}

export function useApproveChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => slotsApi.approveChangeRequest(id),
    onSuccess: () => {
      toast.success("Proposal approved");
      void queryClient.invalidateQueries({ queryKey: ["slots", "change-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["loads"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useRejectChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => slotsApi.rejectChangeRequest(id),
    onSuccess: () => {
      toast.success("Proposal rejected");
      void queryClient.invalidateQueries({ queryKey: ["slots", "change-requests"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}
